/**
 * Shared NET cell for the opener / unwinder / positions tables.
 *
 * Renders the signed `total YES − total NO` exposure (positive = excess YES)
 * off the live engine's `believedPosition`. Green when hedged, red when the
 * signed net exceeds the tolerance (`isUnhedged`). The tooltip breaks the net
 * out into Kalshi net / Poly YES / Poly NO.
 *
 * When no live `believedPosition` is present yet, it falls back to the slower
 * REST position group (per-side `contracts`) and renders the same signed net
 * in a neutral grey — or `—` when neither is available.
 */
import { Box, Tooltip } from '@mui/material';
import type { BelievedPosition, RuleCost } from '../types/entry.types';
import { IMBALANCE_TOLERANCE, isUnhedged, netBreakdown, signedNet } from './unhedged';

/**
 * WP-G parity flag. Default OFF (REST-authoritative): the ACT column keeps
 * reading the 10s REST positions poll until an operator confirms the engine net
 * matches in prod, then opts IN with `VITE_ACT_FROM_ENGINE=true`. When ON, the
 * ACT column reads the engine-pushed live net (`ruleCost.kalshiNet` / `polyNet`)
 * off the socket instead. Opt-in (not opt-out) also de-risks the prior-session
 * net-0 parity gap until reconcile parity is proven.
 */
export const ACT_FROM_ENGINE = import.meta.env.VITE_ACT_FROM_ENGINE === 'true';

/**
 * A `ruleCost` whose `computedAt` is older than this is treated as no longer
 * live (the rule/group stopped streaming, e.g. it finished or the pod stopped
 * emitting) → the ACT cell falls back to the REST-held net rather than freezing
 * a stale engine figure (incl. a stale red "naked" value after a position
 * closed). Mirrors `GroupLiveSummaryStrip`'s staleness window.
 */
export const ACT_STALE_MS = 15_000;

const HEDGED_COLOR = '#51cf66';
const UNHEDGED_COLOR = '#ff6b6b';
const REST_COLOR = 'grey.500';

/** A REST position group with the per-side contract counts NetCell needs. */
export interface RestNetSource {
  positions?: { platform: 'polymarket' | 'kalshi'; side: 'yes' | 'no'; contracts: number }[];
}

const signed = (n: number): string =>
  `${n > 0 ? '+' : ''}${Number.isInteger(n) ? n : n.toFixed(2)}`;

/**
 * Signed `YES − NO` from a REST position group. Polymarket YES + Kalshi YES
 * are positive; Polymarket NO + Kalshi NO are negative. Mirrors the
 * believed-position sign convention (excess-YES positive).
 */
export const restSignedNet = (rest: RestNetSource): number => {
  let net = 0;
  for (const p of rest.positions ?? []) {
    net += p.side === 'yes' ? p.contracts : -p.contracts;
  }
  return net;
};

/**
 * Sign-agnostic size of the actual leg imbalance — `|total YES − total NO|`.
 * This is the number of contracts the operator must still hedge; the magnitude
 * is identical whether the excess sits on YES or NO, so the opener/unwinder
 * tables sort by this absolute value to surface the largest naked positions
 * first regardless of direction.
 */
export const restUnhedgeMagnitude = (rest: RestNetSource): number =>
  Math.abs(restSignedNet(rest));

interface NetCellProps {
  bp: BelievedPosition | null | undefined;
  rest?: RestNetSource;
}

export const NetCell = ({ bp, rest }: NetCellProps) => {
  if (bp) {
    const net = signedNet(bp);
    const unhedged = isUnhedged(bp);
    const { kalshiNet, polyYes, polyNo } = netBreakdown(bp);
    const kLabel =
      kalshiNet >= 0 ? `+${kalshiNet} NO` : `+${Math.abs(kalshiNet)} YES`;
    const title = `K: ${kLabel} · P: YES ${polyYes} / NO ${polyNo}`;
    return (
      <Tooltip title={title} placement="top">
        <Box
          component="span"
          data-net-state={unhedged ? 'unhedged' : 'hedged'}
          data-net-title={title}
          sx={{
            color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {signed(net)}
        </Box>
      </Tooltip>
    );
  }

  if (rest && (rest.positions?.length ?? 0) > 0) {
    return (
      <Box
        component="span"
        data-net-state="rest"
        sx={{ color: REST_COLOR, fontVariantNumeric: 'tabular-nums' }}
      >
        {signed(restSignedNet(rest))}
      </Box>
    );
  }

  return (
    <Box component="span" data-net-state="empty" sx={{ color: REST_COLOR }}>
      —
    </Box>
  );
};

/**
 * REST "actual" NET cell — the signed `YES − NO` exposure read straight from
 * the held `prediction_positions`, independent of the live engine's belief.
 *
 * The live `NetCell` reads `believedPosition`, whose per-path counters only
 * tally contracts the engine filled THIS session — so a pre-existing position
 * (filled in a prior session / before a restart) reads net 0 there even when
 * the actual legs are imbalanced. This cell shows the real held imbalance, so
 * the operator can spot engine-belief-vs-reality drift at a glance. Coloured by
 * the same tolerance: green when hedged, red when the actual net is unhedged.
 */
export const RestNetCell = ({ rest }: { rest?: RestNetSource }) => {
  if (!rest || (rest.positions?.length ?? 0) === 0) {
    return (
      <Box component="span" data-actnet-state="empty" sx={{ color: REST_COLOR }}>
        —
      </Box>
    );
  }
  const net = restSignedNet(rest);
  const unhedged = Math.abs(net) > IMBALANCE_TOLERANCE;
  const title = `Actual held position (total YES − total NO, from positions): ${signed(net)}`;
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-actnet-state={unhedged ? 'unhedged' : 'hedged'}
        sx={{
          color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {signed(net)}
      </Box>
    </Tooltip>
  );
};

/**
 * Signed `YES − NO` net from the engine's live `ruleCost`. `polyNet` (`+` =
 * net-long YES) and `kalshiNet` (`+` = net-long NO) reuse the believed-position
 * sign convention, so total `YES − NO` exposure is `polyNet − kalshiNet` — the
 * same value `restSignedNet` derives from held contracts (parity).
 */
const ruleCostSignedNet = (
  rc: Pick<RuleCost, 'kalshiNet' | 'polyNet'>
): number => rc.polyNet - rc.kalshiNet;

/**
 * ACT ("actual net") cell. Behind the `VITE_ACT_FROM_ENGINE` flag (default OFF, opt-in):
 * renders the engine-pushed live net (`ruleCost.kalshiNet` / `polyNet`) that
 * rides the opener socket, replacing the 10s REST positions poll. Falls back to
 * the REST-held `RestNetCell` when the flag is OFF, when the engine has no
 * `ruleCost` for the pair yet (boot-grace / parity), or when the last
 * `ruleCost` is STALE (`computedAt` older than `ACT_STALE_MS`) — otherwise a
 * rule that stopped streaming would freeze its last engine net indefinitely.
 */
export const ActNetCell = ({
  ruleCost,
  rest,
}: {
  ruleCost?: RuleCost | null;
  rest?: RestNetSource;
}) => {
  const fresh =
    !!ruleCost && Date.now() - ruleCost.computedAt <= ACT_STALE_MS;
  if (ACT_FROM_ENGINE && ruleCost && fresh) {
    const net = ruleCostSignedNet(ruleCost);
    const unhedged = Math.abs(net) > IMBALANCE_TOLERANCE;
    const title = `Engine live net (total YES − total NO): ${signed(net)} · K net ${signed(
      ruleCost.kalshiNet
    )} (+ = NO) · P net ${signed(ruleCost.polyNet)} (+ = YES)`;
    return (
      <Tooltip title={title} placement="top">
        <Box
          component="span"
          data-actnet-state={unhedged ? 'unhedged' : 'hedged'}
          data-actnet-source="engine"
          sx={{
            color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {signed(net)}
        </Box>
      </Tooltip>
    );
  }
  return <RestNetCell rest={rest} />;
};
